import { ErrorHandler, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService } from '../../shared/services/alert/alert.service';
import { MonitoringService } from '../monitoring/monitoring.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  private static readonly RELOAD_FLAG = 'chunk_reload_attempted';
  private static readonly THROTTLE_MS = 2000;
  private static readonly MAX_RETRIES = 2;

  private readonly alertService = inject(AlertService);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly monitoring = inject(MonitoringService);

  private lastAlertTime = 0;

  handleError(error: any): void {
    const chunkFailedPattern = /Loading chunk [\S]+ failed|ChunkLoadError/i;
    const innerError = error?.rejection || error;

    if (chunkFailedPattern.test(innerError?.message || '')) {
      this.handleChunkLoadError(innerError);
      return;
    }

    // Erreurs internes Formio (eval dans les expressions custom) — ni toast ni Sentry
    const msg = innerError?.message || '';
    if (msg.includes('is not a function') && innerError?.stack?.includes('evaluator')) {
      return;
    }

    // Un tableau d'erreurs Formio n'est PAS une Error. Le laisser passer a
    // captureException faisait serialiser par le SDK l'objet entier : schema
    // du formulaire + donnees saisies + profil Keycloak, soit ~283 Ko dans le
    // champ `message` -- champ qu'aucun scrubbing par nom de cle ne couvre.
    // On ne remonte donc que la FORME de l'erreur et les CLES en echec.
    if (!(innerError instanceof Error)) {
      this.monitoring.captureMessage('Erreur non-Error capturee', 'error', {
        tags: { error_shape: Array.isArray(innerError) ? 'array' : typeof innerError },
        extra: {
          formioErrorCount: Array.isArray(innerError) ? innerError.length : undefined,
          // Cles de composants en echec : elles viennent du schema du
          // formulaire, jamais d'une saisie de l'usager.
          formioKeys: Array.isArray(innerError)
            ? innerError
                .map((e: any) => e?.component?.key)
                .filter((k: unknown): k is string => typeof k === 'string')
                .slice(0, 20)
            : undefined,
        },
      });
      this.showErrorAlert(innerError);
      return;
    }

    this.monitoring.captureException(innerError);
    this.showErrorAlert(innerError);
  }

  private showErrorAlert(error: any): void {
    const now = Date.now();
    if (now - this.lastAlertTime < GlobalErrorHandler.THROTTLE_MS) return;
    this.lastAlertTime = now;

    const message = this.extractMessage(error) || 'Une erreur s\'est produite';
    const detail = this.extractDetail(error);

    this.zone.run(() => {
      this.alertService.error(message, detail);
    });
  }

  private extractMessage(error: any): string {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.error?.message) return error.error.message;
    if (error.statusText) return `${error.status} ${error.statusText}`;
    return '';
  }

  private extractDetail(error: any): string {
    const parts: string[] = [];

    if (error?.url) parts.push(error.url);
    if (error?.status) parts.push(`HTTP ${error.status}`);
    if (error?.error?.errors && Array.isArray(error.error.errors)) {
      parts.push(error.error.errors.map((e: any) => e.clean_message || e.message).join(' | '));
    }
    if (error?.stack) {
      const firstLine = error.stack.split('\n').slice(0, 3).join(' <- ');
      parts.push(firstLine);
    }

    return parts.join(' — ') || '';
  }

  private handleChunkLoadError(error?: any): void {
    const retryCount = parseInt(sessionStorage.getItem(GlobalErrorHandler.RELOAD_FLAG) || '0', 10);

    if (retryCount >= GlobalErrorHandler.MAX_RETRIES) {
      sessionStorage.removeItem(GlobalErrorHandler.RELOAD_FLAG);
      // Signal de déploiement cassé : hash de chunk introuvable malgré les retries
      this.monitoring.captureMessage('Chunk load failed after retries', 'warning', {
        tags: { chunk_load: true },
        extra: { message: error?.message }
      });
      this.zone.run(() => {
        this.alertService.error(
          'Le chargement de la page a echoue.',
          'Essayez Ctrl+Shift+R pour forcer le rechargement.'
        );
      });
      return;
    }

    sessionStorage.setItem(GlobalErrorHandler.RELOAD_FLAG, String(retryCount + 1));

    // Retry 1 : re-navigation Angular (recharge le chunk sans perdre l'etat)
    // Retry 2 : full reload en dernier recours
    if (retryCount === 0) {
      this.zone.run(() => {
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(currentUrl);
        });
      });
    } else {
      window.location.reload();
    }
  }
}
